/**
 * Scenario Input Validation Utility
 * 
 * This module provides comprehensive validation for all scenario inputs
 * across different stages (A1, A2, A3, A4-A5, B2-B5, C1-C4).
 * 
 * Validation Rules:
 * 1. All inputs must be >= 0
 * 2. A1 (Quarry): Amount of rocks blasted must equal sum of aggregate sizes
 * 3. A1 aggregate amount must equal A3 aggregate input
 * 4. Prevents stage progression until all validations pass
 */

// ==================== VALIDATION RULES ====================

/**
 * Basic non-negative validation
 */
export const validateNonNegative = (value, fieldName) => {
  const numValue = parseFloat(value);
  
  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: `${fieldName} must be a valid number`
    };
  }
  
  if (numValue < 0) {
    return {
      isValid: false,
      error: `${fieldName} must be greater than or equal to 0`
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Per-ton validation (0 to 1 range)
 * For Quarry and Manufacturing stages - values are per ton of asphalt
 */
export const validatePerTonRange = (value, fieldName) => {
  // Allow empty/undefined values (they default to 0)
  if (value === '' || value === null || value === undefined) {
    return { isValid: true, error: null };
  }
  
  const numValue = parseFloat(value);
  
  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: `${fieldName} must be a valid number`
    };
  }
  
  if (numValue < 0) {
    return {
      isValid: false,
      error: `${fieldName} must be greater than or equal to 0`
    };
  }
  
  if (numValue > 1) {
    return {
      isValid: false,
      error: `${fieldName} must be less than or equal to 1 (values are per ton of asphalt)`
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate that rocks blasted equals sum of aggregate sizes
 */
export const validateRocksBlasted = (quarryData) => {
  const rocksBlasted = parseFloat(quarryData.rocks_blasted || 0);
  
  // Sum of all aggregate sizes
  const aggregateSum = 
    parseFloat(quarryData.amount_3_8_inch || 0) +
    parseFloat(quarryData.amount_1_2_inch || 0) +
    parseFloat(quarryData.amount_1_1_2_inch || 0) +
    parseFloat(quarryData.amount_3_4_inch || 0) +
    parseFloat(quarryData.amount_c33 || 0);
  
  const tolerance = 0.0001; // Small tolerance for floating point comparison
  
  if (Math.abs(rocksBlasted - aggregateSum) > tolerance) {
    return {
      isValid: false,
      error: `Rocks Blasted (${rocksBlasted.toFixed(4)} tons) does not match the total of all aggregate sizes (${aggregateSum.toFixed(4)} tons). The five aggregate amounts must add up to equal Rocks Blasted. Click Auto-Fix to correct.`,
      expected: aggregateSum,
      actual: rocksBlasted
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate that A1 aggregate amount equals A3 aggregate input
 * Rocks Blasted (A1) should equal Amount of Aggregates (A3)
 */
export const validateAggregateConsistency = (quarryData, manufacturingData) => {
  const a1AggregateSum = 
    parseFloat(quarryData.amount_3_8_inch || 0) +
    parseFloat(quarryData.amount_1_2_inch || 0) +
    parseFloat(quarryData.amount_1_1_2_inch || 0) +
    parseFloat(quarryData.amount_3_4_inch || 0) +
    parseFloat(quarryData.amount_c33 || 0);
  
  const rocksBlasted = parseFloat(quarryData.rocks_blasted || 0);
  
  // A3 Amount of Aggregates field (direct comparison)
  const a3AggregateAmount = parseFloat(manufacturingData.amount_aggregates || 0);
  
  // Skip validation if manufacturing data is not filled yet
  if (a3AggregateAmount === 0 && !manufacturingData.amount_aggregates) {
    return { isValid: true, error: null };
  }
  
  const tolerance = 0.0001;
  
  // First check: Rocks Blasted should equal A3 Amount of Aggregates
  if (Math.abs(rocksBlasted - a3AggregateAmount) > tolerance) {
    return {
      isValid: false,
      error: `Quarry Rocks Blasted (${rocksBlasted.toFixed(4)} tons) must equal Manufacturing Amount of Aggregates (${a3AggregateAmount.toFixed(4)} tons). These values represent the same virgin aggregates used in production.`,
      a1RocksBlasted: rocksBlasted,
      a3Aggregates: a3AggregateAmount
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate that rocks blasted is between 0 and 1 (normalized)
 * Note: This assumes normalized values. Remove if using actual tonnage.
 */
export const validateRocksBlastValue = (value) => {
  const numValue = parseFloat(value);
  
  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: 'Rocks blasted must be a valid number'
    };
  }
  
  if (numValue < 0 || numValue > 1) {
    return {
      isValid: false,
      error: 'Rocks blasted must be between 0 and 1'
    };
  }
  
  return { isValid: true, error: null };
};

// ==================== STAGE VALIDATORS ====================

/**
 * Validate all A1 (Quarry) stage inputs
 */
export const validateQuarryStage = (quarryData) => {
  const errors = {};
  const warnings = [];
  
  // Define all quarry fields that need validation
  const quarryFields = {
    // Drilling
    'renewable_diesel_sandvik_1': 'Renewable Diesel Sandvik 1',
    'renewable_diesel_sandvik_2': 'Renewable Diesel Sandvik 2',
    'drilling_dust': 'Drilling Dust',
    
    // Blasting
    'rocks_blasted': 'Rocks Blasted',
    'amount_3_8_inch': 'Amount 3/8" Aggregate',
    'amount_1_2_inch': 'Amount 1/2" Aggregate',
    'amount_1_1_2_inch': 'Amount 1-1/2" Aggregate',
    'amount_3_4_inch': 'Amount 3/4" Aggregate',
    'amount_c33': 'Amount C33 Aggregate',
    'subtek_eclipse': 'Subtek Eclipse',
    'blasting_dust': 'Blasting Dust',
    
    // Crushing
    'rocks_to_crush': 'Rocks to Crush',
    'electricity_primary_crusher_telsmith': 'Primary Crusher Telsmith',
    'electricity_primary_crusher_lippman': 'Primary Crusher Lippman',
    'electricity_secondary_crusher_telsmith': 'Secondary Crusher',
    'electricity_tertiary_crusher_telsmith': 'Tertiary Crusher',
    'electricity_quaternary_crusher_telsmith': 'Quaternary Crusher',
    'electricity_feeder_telsmith': 'Feeder Telsmith',
    'electricity_feeder_lippman': 'Feeder Lippman',
    'crushing_dust': 'Crushing Dust',
    
    // Processing
    'crushed_rock_processing': 'Crushed Rock Processing',
    'electricity_scalping_screen_telsmith': 'Scalping Screen Telsmith',
    'electricity_scalping_screen_simplicity': 'Scalping Screen Simplicity',
    'electricity_tertiary_screen': 'Tertiary Screen',
    'electricity_washscreen': 'Wash Screen',
    'electricity_dewatering_screen': 'Dewatering Screen',
    'electricity_wash_tank': 'Wash Tank',
    'electricity_fine_material_screw': 'Fine Material Screw',
    'fuel_water_trucks': 'Fuel Water Trucks',
    
    // Refining
    'crushed_rock_refining': 'Crushed Rock Refining',
    'water_sprinkle_stalk_pile': 'Water Sprinkle',
    'refining_dust': 'Refining Dust',
    
    // Raw materials
    'amount_pg_64_16': 'PG 64-16',
    'amount_pg_64_10': 'PG 64-10',
    'amount_pg_64_28': 'PG 64-28',
    'amount_ra_5': 'RA-5',
    'amount_cld_mix': 'CLD Mix',
    'amount_rubber': 'Rubber',
    'amount_eco_spa': 'Eco SPA',
    'amount_pavegrip': 'PaveGrip',
    'amount_latex': 'Latex',
    'amount_evotherm': 'Evotherm',
    'amount_morlife': 'Morlife',
    'amount_evoflex_ca6': 'Evoflex CA6',
    'amount_fiber': 'Fiber',
    'amount_rejuvenator': 'Rejuvenator',
    'amount_anti_strip': 'Anti-Strip'
  };
  
  // Validate all fields are non-negative AND per-ton range (0-1)
  Object.keys(quarryFields).forEach(field => {
    const validation = validatePerTonRange(quarryData[field], quarryFields[field]);
    if (!validation.isValid) {
      errors[field] = validation.error;
    }
  });
  
  // NOTE: Aggregate sum validation (rocks_blasted = sum of aggregates) 
  // is now only done in Manufacturing stage, not here in Quarry
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings
  };
};

/**
 * Validate all A2 (Transport) stage inputs
 */
export const validateTransportStage = (transportData) => {
  const errors = {};
  
  const transportFields = {
    // Loaders
    'loader_992_fuel_rate': 'Loader 992 Fuel Rate',
    'loader_992_hours': 'Loader 992 Hours',
    'loader_990_fuel_rate': 'Loader 990 Fuel Rate',
    'loader_990_hours': 'Loader 990 Hours',
    'loader_aggregate_fuel_rate': 'Aggregate Loader Fuel Rate',
    'loader_aggregate_hours': 'Aggregate Loader Hours',
    'loader_midsize_fuel_rate': 'Midsize Loader Fuel Rate',
    'loader_midsize_hours': 'Midsize Loader Hours',
    
    // Heavy equipment
    'bulldozer_fuel_rate': 'Bulldozer Fuel Rate',
    'bulldozer_hours': 'Bulldozer Hours',
    'excavator_mining_fuel_rate': 'Mining Excavator Fuel Rate',
    'excavator_mining_hours': 'Mining Excavator Hours',
    'hydraulic_shovel_fuel_rate': 'Hydraulic Shovel Fuel Rate',
    'hydraulic_shovel_hours': 'Hydraulic Shovel Hours',
    'cat_988k_fuel_rate': 'CAT 988K Fuel Rate',
    'cat_988k_hours': 'CAT 988K Hours',
    
    // Transport
    'haul_truck_offroad_fuel_consumption': 'Haul Truck Fuel',
    'truck_binder_fuel_consumption': 'Binder Truck Fuel',
    'truck_rap_fuel_consumption': 'RAP Truck Fuel',
    'truck_rejuvenators_round_trip_distance': 'Rejuvenators Distance',
    'truck_rejuvenators_capacity_divisor': 'Rejuvenators Capacity',
    'truck_rejuvenators_trips_per_day': 'Rejuvenators Trips',
    'truck_antistrip_round_trip_distance': 'Anti-Strip Distance',
    'truck_antistrip_capacity_divisor': 'Anti-Strip Capacity',
    'truck_antistrip_trips_per_day': 'Anti-Strip Trips',
    'truck_rubber__fuel_consumption': 'Rubber Truck Fuel',
    'truck_raw_materials_round_trip_distance': 'Raw Materials Distance',
    'truck_raw_materials_capacity_divisor': 'Raw Materials Capacity',
    'rail_raw_materials_round_trip_distance': 'Rail Materials Distance',
    'rail_raw_materials_capacity_divisor': 'Rail Materials Capacity'
  };
  
  Object.keys(transportFields).forEach(field => {
    const validation = validateNonNegative(transportData[field], transportFields[field]);
    if (!validation.isValid) {
      errors[field] = validation.error;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate all A3 (Manufacturing) stage inputs
 * NOTE: Only validates USER INPUT fields, not facility parameters
 */
export const validateManufacturingStage = (manufacturingData, quarryData = null) => {
  const errors = {};
  const warnings = [];
  
  // USER INPUT FIELDS ONLY (visible to users in the form)
  // Parameters (like electricity consumption rates) are NOT validated here
  const userInputFields = {
    'production_target': 'Production Target',
    'amount_aggregates': 'Amount of Aggregates',
    'amount_rap': 'Amount of RAP',
    'amount_ras': 'Amount of RAS',
    'amount_rubber': 'Amount of Rubber'
  };
  
  Object.keys(userInputFields).forEach(field => {
    // Production target is NOT per-ton, so only check non-negative
    if (field === 'production_target') {
      const validation = validateNonNegative(manufacturingData[field], userInputFields[field]);
      if (!validation.isValid) {
        errors[field] = validation.error;
      }
    } else {
      // All other USER INPUT fields are per-ton, so check 0-1 range
      const validation = validatePerTonRange(manufacturingData[field], userInputFields[field]);
      if (!validation.isValid) {
        errors[field] = validation.error;
      }
    }
  });
  
  // Cross-stage validation: A1 Rocks Blasted = A3 Amount of Aggregates
  if (quarryData) {
    const aggregateValidation = validateAggregateConsistency(quarryData, manufacturingData);
    if (!aggregateValidation.isValid) {
      warnings.push({
        type: 'aggregate_consistency',
        message: aggregateValidation.error,
        a1RocksBlasted: aggregateValidation.a1RocksBlasted,
        a3Aggregates: aggregateValidation.a3Aggregates
      });
      errors.aggregate_consistency = aggregateValidation.error;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings
  };
};

/**
 * Validate all A4-A5 (Transport from Plant and Installation) stage inputs
 * NOTE: Only validates USER INPUT fields visible to users
 */
export const validateInstallationStage = (installationData) => {
  const errors = {};
  
  // USER INPUT FIELD ONLY (what users actually see and fill)
  const userInputFields = {
    'total_diesel_consumption': 'Total Diesel Consumption for Transport to Site'
  };
  
  Object.keys(userInputFields).forEach(field => {
    const validation = validateNonNegative(installationData[field], userInputFields[field]);
    if (!validation.isValid) {
      errors[field] = validation.error;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate B2-B5 (Use Phase) stage inputs
 */
export const validateUsePhaseStage = (usePhaseData) => {
  const errors = {};
  
  const usePhaseFields = {
    'maintenance_frequency': 'Maintenance Frequency',
    'energy_consumption': 'Energy Consumption',
    'material_consumption': 'Material Consumption'
  };
  
  Object.keys(usePhaseFields).forEach(field => {
    if (usePhaseData[field] !== undefined) {
      const validation = validateNonNegative(usePhaseData[field], usePhaseFields[field]);
      if (!validation.isValid) {
        errors[field] = validation.error;
      }
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate C1-C4 (End of Life) stage inputs
 */
export const validateEndOfLifeStage = (endOfLifeData) => {
  const errors = {};
  
  const endOfLifeFields = {
    'deconstruction_energy': 'Deconstruction Energy',
    'transport_distance': 'Transport Distance',
    'landfill_emissions': 'Landfill Emissions',
    'recycling_rate': 'Recycling Rate'
  };
  
  Object.keys(endOfLifeFields).forEach(field => {
    if (endOfLifeData[field] !== undefined) {
      const validation = validateNonNegative(endOfLifeData[field], endOfLifeFields[field]);
      if (!validation.isValid) {
        errors[field] = validation.error;
      }
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// ==================== COMPREHENSIVE VALIDATORS ====================

/**
 * Validate entire scenario across all stages
 */
export const validateCompleteScenario = (scenarioData) => {
  const validationResults = {
    isValid: true,
    stages: {},
    criticalErrors: [],
    warnings: []
  };
  
  // Validate A1 - Quarry
  if (scenarioData.quarry) {
    const quarryValidation = validateQuarryStage(scenarioData.quarry);
    validationResults.stages.quarry = quarryValidation;
    
    if (!quarryValidation.isValid) {
      validationResults.isValid = false;
      validationResults.criticalErrors.push('Quarry stage has validation errors');
    }
    
    if (quarryValidation.warnings) {
      validationResults.warnings.push(...quarryValidation.warnings);
    }
  }
  
  // Validate A2 - Transport
  if (scenarioData.transport) {
    const transportValidation = validateTransportStage(scenarioData.transport);
    validationResults.stages.transport = transportValidation;
    
    if (!transportValidation.isValid) {
      validationResults.isValid = false;
      validationResults.criticalErrors.push('Transport stage has validation errors');
    }
  }
  
  // Validate A3 - Manufacturing
  if (scenarioData.manufacturing) {
    const manufacturingValidation = validateManufacturingStage(
      scenarioData.manufacturing,
      scenarioData.quarry
    );
    validationResults.stages.manufacturing = manufacturingValidation;
    
    if (!manufacturingValidation.isValid) {
      validationResults.isValid = false;
      validationResults.criticalErrors.push('Manufacturing stage has validation errors');
    }
    
    if (manufacturingValidation.warnings) {
      validationResults.warnings.push(...manufacturingValidation.warnings);
    }
  }
  
  // Validate A4-A5 - Installation
  if (scenarioData.installation) {
    const installationValidation = validateInstallationStage(scenarioData.installation);
    validationResults.stages.installation = installationValidation;
    
    if (!installationValidation.isValid) {
      validationResults.isValid = false;
      validationResults.criticalErrors.push('Installation stage has validation errors');
    }
  }
  
  // Validate B2-B5 - Use Phase
  if (scenarioData.usePhase) {
    const usePhaseValidation = validateUsePhaseStage(scenarioData.usePhase);
    validationResults.stages.usePhase = usePhaseValidation;
    
    if (!usePhaseValidation.isValid) {
      validationResults.isValid = false;
      validationResults.criticalErrors.push('Use phase stage has validation errors');
    }
  }
  
  // Validate C1-C4 - End of Life
  if (scenarioData.endOfLife) {
    const endOfLifeValidation = validateEndOfLifeStage(scenarioData.endOfLife);
    validationResults.stages.endOfLife = endOfLifeValidation;
    
    if (!endOfLifeValidation.isValid) {
      validationResults.isValid = false;
      validationResults.criticalErrors.push('End of life stage has validation errors');
    }
  }
  
  return validationResults;
};

/**
 * Check if stage can proceed (used for wizard navigation)
 */
export const canProceedToNextStage = (currentStage, scenarioData) => {
  switch (currentStage) {
    case 'quarry':
    case 'A1':
      const quarryValidation = validateQuarryStage(scenarioData.quarry || {});
      return quarryValidation.isValid;
      
    case 'transport':
    case 'A2':
      const transportValidation = validateTransportStage(scenarioData.transport || {});
      return transportValidation.isValid;
      
    case 'manufacturing':
    case 'A3':
      const manufacturingValidation = validateManufacturingStage(
        scenarioData.manufacturing || {},
        scenarioData.quarry
      );
      return manufacturingValidation.isValid;
      
    case 'installation':
    case 'A4-A5':
      const installationValidation = validateInstallationStage(scenarioData.installation || {});
      return installationValidation.isValid;
      
    case 'usePhase':
    case 'B2-B5':
      const usePhaseValidation = validateUsePhaseStage(scenarioData.usePhase || {});
      return usePhaseValidation.isValid;
      
    case 'endOfLife':
    case 'C1-C4':
      const endOfLifeValidation = validateEndOfLifeStage(scenarioData.endOfLife || {});
      return endOfLifeValidation.isValid;
      
    default:
      return true;
  }
};

/**
 * Get validation errors for display
 */
export const getValidationErrors = (stage, data, previousStageData = null) => {
  switch (stage) {
    case 'quarry':
    case 'A1':
      return validateQuarryStage(data);
      
    case 'transport':
    case 'A2':
      return validateTransportStage(data);
      
    case 'manufacturing':
    case 'A3':
      return validateManufacturingStage(data, previousStageData);
      
    case 'installation':
    case 'A4-A5':
      return validateInstallationStage(data);
      
    case 'usePhase':
    case 'B2-B5':
      return validateUsePhaseStage(data);
      
    case 'endOfLife':
    case 'C1-C4':
      return validateEndOfLifeStage(data);
      
    default:
      return { isValid: true, errors: {} };
  }
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Format validation errors for user display
 */
export const formatValidationErrors = (validationResult) => {
  if (validationResult.isValid) {
    return null;
  }
  
  const errorMessages = [];
  
  Object.entries(validationResult.errors).forEach(([field, message]) => {
    errorMessages.push(message);
  });
  
  return errorMessages;
};

/**
 * Calculate sum of aggregate sizes from quarry data
 */
export const calculateAggregateSum = (quarryData) => {
  return (
    parseFloat(quarryData.amount_3_8_inch || 0) +
    parseFloat(quarryData.amount_1_2_inch || 0) +
    parseFloat(quarryData.amount_1_1_2_inch || 0) +
    parseFloat(quarryData.amount_3_4_inch || 0) +
    parseFloat(quarryData.amount_c33 || 0)
  );
};

/**
 * Auto-fix rocks blasted to match aggregate sum
 */
export const autoFixRocksBlasted = (quarryData) => {
  const aggregateSum = calculateAggregateSum(quarryData);
  return {
    ...quarryData,
    rocks_blasted: aggregateSum
  };
};

export default {
  validateNonNegative,
  validateRocksBlasted,
  validateAggregateConsistency,
  validateQuarryStage,
  validateTransportStage,
  validateManufacturingStage,
  validateInstallationStage,
  validateUsePhaseStage,
  validateEndOfLifeStage,
  validateCompleteScenario,
  canProceedToNextStage,
  getValidationErrors,
  formatValidationErrors,
  calculateAggregateSum,
  autoFixRocksBlasted
};